import { createTheme } from '@mui/material/styles';
import { colors, FONT_FAMILY } from './designTokens';

const theme = createTheme({
  palette: {
    primary: {
      main: '#1d4ed8',
      light: '#1d4ed8',
      dark: '#1e40af',
      50: 'rgba(29, 78, 216, 0.08)',
      100: 'rgba(29, 78, 216, 0.12)',
    },
    secondary: {
      main: '#f50057',
      light: '#ff4081',
      dark: '#c51162',
    },
    background: {
      default: 'rgba(29, 78, 216, 0.045)',
      paper: '#ffffff',
    },
    text: {
      primary: colors.textPrimary,
      secondary: colors.textSecondary,
    },
  },
  typography: {
    fontFamily: FONT_FAMILY,
    h1: {
      fontSize: '2rem',
      fontWeight: 650,
      letterSpacing: '-0.02em',
      lineHeight: 1.2,
    },
    h2: {
      fontSize: '1.75rem',
      fontWeight: 650,
      letterSpacing: '-0.02em',
      lineHeight: 1.25,
    },
    h3: {
      fontSize: '1.5rem',
      fontWeight: 600,
      letterSpacing: '-0.015em',
      lineHeight: 1.3,
    },
    h4: {
      fontSize: '1.875rem',
      fontWeight: 650,
      letterSpacing: '-0.02em',
      lineHeight: 1.25,
    },
    h5: {
      fontSize: '1.375rem',
      fontWeight: 600,
      letterSpacing: '-0.015em',
      lineHeight: 1.3,
    },
    h6: {
      fontSize: '1.125rem',
      fontWeight: 600,
      letterSpacing: '-0.01em',
      lineHeight: 1.35,
      color: colors.textPrimary,
    },
    subtitle1: {
      fontSize: '1rem',
      fontWeight: 600,
      letterSpacing: '-0.01em',
      lineHeight: 1.4,
    },
    subtitle2: {
      fontSize: '0.875rem',
      fontWeight: 500,
      color: colors.textSecondary,
      lineHeight: 1.45,
    },
    body1: {
      fontSize: '0.875rem',
      fontWeight: 400,
      lineHeight: 1.6,
      color: colors.textPrimary,
    },
    body2: {
      fontSize: '0.875rem',
      fontWeight: 400,
      lineHeight: 1.55,
      color: colors.textSecondary,
    },
    caption: {
      fontSize: '0.8125rem',
      fontWeight: 400,
      lineHeight: 1.45,
      color: colors.textMuted,
    },
    button: {
      fontWeight: 600,
      textTransform: 'none',
      letterSpacing: '0.01em',
    },
  },
  shape: {
    borderRadius: 10,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          fontFamily: FONT_FAMILY,
          color: colors.textPrimary,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 16,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#1d4ed8',
          color: '#ffffff',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRadius: 0,
          backgroundColor: '#ffffff',
          borderRight: `1px solid ${colors.cardBorder}`,
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 10,
          fontWeight: 600,
          transition: 'all 160ms ease',
        },
        contained: {
          boxShadow: '0 1px 2px rgba(15,23,42,.06)',
          '&:hover': {
            boxShadow: '0 2px 6px rgba(15,23,42,.1)',
          },
        },
        outlined: {
          borderColor: '#E2E8F0',
          '&:hover': {
            borderColor: '#CBD5E1',
            backgroundColor: 'rgba(29, 78, 216, 0.04)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 14,
          border: `1px solid ${colors.cardBorder}`,
          boxShadow: 'none',
          transition: 'border-color 180ms ease, box-shadow 180ms ease',
          '&:hover': {
            borderColor: colors.cardBorderHover,
            boxShadow: '0 2px 8px rgba(15, 23, 42, 0.04)',
          },
        },
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        root: {
          paddingTop: 14,
          paddingBottom: 10,
          borderBottom: `1px solid ${colors.cardBorder}`,
          background: 'linear-gradient(180deg, rgba(248,250,252,0.8) 0%, rgba(248,250,252,0.35) 100%)',
        },
        title: {
          fontSize: '0.8125rem',
          fontWeight: 600,
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
          color: colors.textSecondary,
        },
        subheader: {
          color: colors.textMuted,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: '#ffffff',
          transition: 'all 160ms ease',
          '&:hover': {
            backgroundColor: '#f8fafc',
          },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#E2E8F0',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#1d4ed8',
            borderWidth: 2,
          },
          '&.Mui-focused': {
            boxShadow: '0 0 0 3px rgba(29, 78, 216, 0.1)',
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          color: '#111827 !important',
          '&.MuiSelect-select': {
            color: '#111827 !important',
          },
        },
        icon: {
          color: '#111827 !important',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          color: '#111827 !important',
          '& .MuiListItemText-primary, & .MuiTypography-root': {
            color: 'inherit !important',
          },
          '&.Mui-disabled': {
            color: 'rgba(17, 24, 39, 0.45)',
          },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        option: {
          color: '#111827 !important',
        },
        input: {
          color: '#111827 !important',
        },
      },
    },
    MuiListSubheader: {
      styleOverrides: {
        root: {
          color: '#111827 !important',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 999,
          border: '1px solid #E2E8F0',
          backgroundColor: 'transparent',
          color: colors.textSecondary,
          fontWeight: 500,
          fontSize: '0.8125rem',
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          height: 2,
          borderRadius: 2,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          minHeight: 42,
          fontSize: '0.875rem',
          transition: 'color 150ms ease',
          '&:hover': {
            color: '#1d4ed8',
          },
          '&.Mui-selected': {
            fontWeight: 600,
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 14,
          boxShadow: '0 20px 45px rgba(15, 23, 42, 0.12)',
          '@media (max-width:599.95px)': {
            width: '100vw',
            maxWidth: '100vw',
            height: '100dvh',
            maxHeight: '100dvh',
            margin: 0,
            borderRadius: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          },
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          letterSpacing: '-0.01em',
          '@media (max-width:599.95px)': {
            flexShrink: 0,
            paddingTop: 'max(12px, env(safe-area-inset-top))',
            paddingLeft: 16,
            paddingRight: 16,
          },
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          '@media (max-width:599.95px)': {
            minHeight: 0,
            WebkitOverflowScrolling: 'touch',
          },
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          '@media (max-width:599.95px)': {
            flexShrink: 0,
            paddingBottom: 'max(12px, env(safe-area-inset-bottom))',
          },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        standardInfo: {
          backgroundColor: 'rgba(29, 78, 216, 0.08)',
          color: '#1e3a8a',
          '& .MuiAlert-icon': {
            color: '#1d4ed8',
          },
        },
        filledInfo: {
          backgroundColor: '#1d4ed8',
        },
      },
    },
  },
});

export default theme;
